import { createContext, useContext, useState, useCallback } from 'react';
import CarritoRepositoryImp from '../../app/infrastructure/carrito/carrito-implementation';
import { dispatchCustomEvent } from '../../utils/dispatch';

const CarritoContext = createContext();

/**
 * CarritoProvider - Gestión completa del carrito de compras
 *
 * Este provider encapsula toda la lógica del carrito:
 * - Estado (car, taxGroup, total)
 * - Operaciones (add, remove, update, clear)
 * - Cálculos (taxes, totals)
 * - Notificaciones (eventos legacy para compatibilidad)
 *
 * Arquitectura:
 * - Usa CarritoRepositoryImp como capa de persistencia
 * - Mantiene estado sincronizado en React
 * - Emite eventos para componentes legacy que aún los necesiten
 */
export function CarritoProvider({ children }) {
  const [car, setCar] = useState([]);
  const [taxGroup, setTaxGroup] = useState([]);
  const [total, setTotal] = useState(0);
  const [type, setTypeState] = useState('sell'); // 'sell' o 'buy'
  const [xmlInvoiceData, setXmlInvoiceDataState] = useState(null);

  const carritoRepo = CarritoRepositoryImp.getInstance();

  /**
   * Sincroniza el estado de React con el repositorio y emite eventos
   */
  const syncState = useCallback(() => {
    const newCar = carritoRepo.getSync();
    const newTaxGroup = carritoRepo.getTaxesSync();
    const newTotal = carritoRepo.getTotalSync();

    setCar([...newCar]);
    setTaxGroup([...newTaxGroup]);
    setTotal(newTotal);

    // Emitir eventos para compatibilidad con componentes legacy
    const data = { car: newCar, tax: newTaxGroup, total: newTotal };
    dispatchCustomEvent('update-sellmodule', data);
    dispatchCustomEvent('update-paymodule', data);
    dispatchCustomEvent('update-badge-pos-pay', {
      badge: getItemsAmount(newCar),
    });
  }, [carritoRepo]);

  /**
   * Calcula la cantidad total de items en el carrito
   */
  const getItemsAmount = useCallback(
    (cartItems = car) => {
      return cartItems.reduce((amount, item) => amount + item.amount, 0);
    },
    [car]
  );

  /**
   * Establece el tipo de operación (sell/buy)
   */
  const setType = useCallback(
    (newType) => {
      setTypeState(newType);
      carritoRepo.setTypeSync(newType);
    },
    [carritoRepo]
  );

  /**
   * Agrega un item al carrito
   * Si el item ya existe, incrementa su cantidad
   */
  const addItem = useCallback(
    (item) => {
      const auxCar = carritoRepo.getSync();
      let itemExist = false;
      let itemAux = { ...item };

      const types = carritoRepo.getTypesSync();
      const currentType = carritoRepo.getTypeSync();

      // Verificar si el item ya existe
      auxCar.forEach((cartItem) => {
        if (cartItem.auxsku === `${itemAux.sku}-tmp`) {
          itemExist = true;
          itemAux = cartItem;
          return;
        }
      });

      if (itemExist) {
        // Incrementar cantidad
        itemAux.amount += 1;
        itemAux.total =
          parseFloat(itemAux[types['value'][currentType]]) *
          parseFloat(itemAux.amount);
        itemAux['auxtax'] =
          itemAux.total -
          itemAux.total /
            (1 + parseFloat(itemAux[types['tax'][currentType]]?.value ?? 0));
        carritoRepo.updateSync(itemAux.auxsku, 'amount', itemAux.amount);
      } else {
        // Agregar nuevo item
        itemAux['auxsku'] = itemAux.sku + '-tmp';
        itemAux['amount'] = 1;
        itemAux['total'] =
          parseFloat(itemAux[types['value'][currentType]]) *
          parseFloat(itemAux.amount);
        itemAux['auxtax'] =
          itemAux['total'] -
          itemAux['total'] /
            (1 + parseFloat(itemAux[types['tax'][currentType]]?.value ?? 0));
        carritoRepo.addSync(itemAux);
      }

      // Advertencia de stock bajo (solo para ventas)
      if (itemAux.amount > itemAux.stock && currentType === 'sell') {
        dispatchCustomEvent('popup', {
          message: `Tienes ${itemAux.stock} ${itemAux.name} en inventario`,
          type: 'warning',
        });
      }

      syncState();
    },
    [carritoRepo, syncState]
  );

  /**
   * Agrega un item personalizado al carrito
   */
  const addCustomItem = useCallback(
    (item) => {
      const itemAux = { ...item };
      if (!itemAux['auxsku']) {
        itemAux['auxsku'] = itemAux.sku + '-tmp';
      }
      carritoRepo.addSync(itemAux);
      syncState();
    },
    [carritoRepo, syncState]
  );

  /**
   * Clona un item con un precio personalizado
   */
  const cloneItem = useCallback(
    (item, precio) => {
      const types = carritoRepo.getTypesSync();
      const currentType = carritoRepo.getTypeSync();

      const itemAux = { ...item };
      itemAux['value'] = precio;
      itemAux['auxsku'] =
        itemAux.sku +
        '-tmp-' +
        Math.floor(Math.random() * 16777215).toString(16);
      itemAux['amount'] = 1;
      itemAux['total'] = parseFloat(itemAux.value) * parseFloat(itemAux.amount);
      itemAux['auxtax'] =
        itemAux['total'] -
        itemAux['total'] /
          (1 + parseFloat(itemAux[types['tax'][currentType]]?.value ?? 0));

      carritoRepo.addSync(itemAux);
      syncState();
    },
    [carritoRepo, syncState]
  );

  /**
   * Actualiza un campo de un item en el carrito
   */
  const updateItem = useCallback(
    (auxsku, key, value) => {
      carritoRepo.updateSync(auxsku, key, value);

      const updatedCar = carritoRepo.getSync();
      const itemAux = updatedCar.find((item) => item.auxsku === auxsku);
      const currentType = carritoRepo.getTypeSync();

      // Advertencia de stock bajo (solo para ventas)
      if (itemAux && itemAux.amount > itemAux.stock && currentType === 'sell') {
        dispatchCustomEvent('popup', {
          message: `Tienes ${itemAux.stock} ${itemAux.name} en inventario`,
          type: 'warning',
        });
      }

      syncState();
    },
    [carritoRepo, syncState]
  );

  /**
   * Elimina un item del carrito
   */
  const removeItem = useCallback(
    (item) => {
      carritoRepo.removeSync(item.auxsku);
      syncState();
    },
    [carritoRepo, syncState]
  );

  /**
   * Guarda los datos de la factura XML (CUFE, número, proveedor) para
   * que el formulario de pago los incluya en el payload de creación de compra.
   */
  const setXmlInvoiceData = useCallback((data) => {
    setXmlInvoiceDataState(data);
  }, []);

  /**
   * Limpia completamente el carrito
   */
  const clearCart = useCallback(() => {
    carritoRepo.clearSync();
    setXmlInvoiceDataState(null);
    syncState();

    // Eventos para resetear UI
    dispatchCustomEvent('update-show-pos-pay', {});
    dispatchCustomEvent('btn-thirds-add', { detail: { data: undefined } });
    dispatchCustomEvent('cancel-transaction', {});
  }, [carritoRepo, syncState]);

  /**
   * Llena el carrito con múltiples items (usado para cargar carritos guardados o editar compras)
   */
  const fillCart = useCallback(
    (items, newType = null) => {
      if (newType) {
        setType(newType);
      }
      carritoRepo.clearSync();
      items.forEach((item) => {
        carritoRepo.addSync(item);
      });
      syncState();
    },
    [carritoRepo, setType, syncState]
  );

  const value = {
    // Estado
    car,
    taxGroup,
    total,
    type,
    xmlInvoiceData,

    // Acciones
    addItem,
    addCustomItem,
    cloneItem,
    updateItem,
    removeItem,
    clearCart,
    fillCart,
    setType,
    setXmlInvoiceData,

    // Utilidades
    getItemsAmount: () => getItemsAmount(car),
  };

  return (
    <CarritoContext.Provider value={value}>{children}</CarritoContext.Provider>
  );
}

/**
 * useCarrito - Hook personalizado para acceder al carrito
 *
 * @returns {Object} Estado y acciones del carrito
 * @throws {Error} Si se usa fuera de un CarritoProvider
 *
 * @example
 * const { car, addItem, removeItem, clearCart } = useCarrito();
 */
export function useCarrito() {
  const context = useContext(CarritoContext);

  if (context === undefined) {
    throw new Error('useCarrito debe ser usado dentro de un CarritoProvider');
  }

  return context;
}
